/**
 * BrandLogo — Reusable workspace logo component.
 *
 * Reads branding state from `useBrandTheme()`. When Custom Branding is
 * enabled it renders the workspace logo (with dark-mode and compact
 * variants); otherwise it falls back to the official AX1 icon mark.
 *
 * @example
 *   <BrandLogo variant="full" />        // sidebar expanded / header
 *   <BrandLogo variant="compact" />     // sidebar collapsed / mobile
 *   <BrandLogo variant="auth" />        // login / unauthorized pages
 */

import React from 'react';
import { useBrandTheme } from '../contexts/BrandThemeContext';
import { cn } from '../lib/utils';
import { PRODUCT_NAME_DISPLAY } from '../brand';
import AX1Logo from './ui/icons/AX1Logo';

export type BrandLogoVariant = 'full' | 'compact' | 'auth';

interface BrandLogoProps {
    variant?: BrandLogoVariant;
    className?: string;
}

/**
 * Optical sizes (64px header / ~448px auth card):
 * - full  28px ≈ 44% of header — sits with 20px shell icons without crowding
 * - compact 24px — denser collapsed chrome
 * - auth  48px — matches the lock circle so identity ≠ headline
 */
const SIZES: Record<BrandLogoVariant, { img: string; icon: string; wrap: string }> = {
    full: { img: 'max-h-7 max-w-[96px]', icon: 'h-7 w-auto', wrap: 'gap-0' },
    compact: { img: 'max-h-6 max-w-[72px]', icon: 'h-6 w-auto', wrap: 'gap-0' },
    auth: { img: 'max-h-12 max-w-[140px]', icon: 'h-12 w-auto', wrap: 'gap-0' },
};

const BrandLogo: React.FC<BrandLogoProps> = ({ variant = 'full', className }) => {
    const { isWhiteLabelEnabled, logoSettings } = useBrandTheme();
    const s = SIZES[variant];

    const isDark = typeof window !== 'undefined' && document.documentElement.classList.contains('dark');
    const isCompact = variant === 'compact';

    let logoSrc: string | null = null;
    if (isWhiteLabelEnabled) {
        if (isCompact && logoSettings.mobileLogoPreview) {
            logoSrc = logoSettings.mobileLogoPreview;
        } else if (isDark && logoSettings.darkLogoPreview) {
            logoSrc = logoSettings.darkLogoPreview;
        } else if (logoSettings.logoPreview) {
            logoSrc = logoSettings.logoPreview;
        }
    }

    if (isWhiteLabelEnabled && logoSrc) {
        const pad = logoSettings.logoPadding;
        return (
            <div className={cn('flex items-center', s.wrap, className)}>
                <img
                    src={logoSrc}
                    alt={logoSettings.logoAltText || 'Logo'}
                    className={cn(s.img, 'object-contain')}
                    style={{
                        padding: `${pad.top}px ${pad.right}px ${pad.bottom}px ${pad.left}px`,
                    }}
                />
            </div>
        );
    }

    return (
        <div className={cn('flex items-center justify-center', s.wrap, className)}>
            <AX1Logo
                className={s.icon}
                fill="var(--ax1-ui-primary-500)"
                aria-label={PRODUCT_NAME_DISPLAY}
            />
        </div>
    );
};

export default BrandLogo;
